import { Link } from "react-router-dom";

export default function OvertimeTipsPost() {
    return (
        <>
            <p>
                In July 2025, Congress passed the <strong>One Big Beautiful Bill Act
                (OBBBA)</strong>, creating two of the most significant tax breaks for
                hourly workers in a generation: the &ldquo;No Tax on Overtime&rdquo;
                deduction and the &ldquo;No Tax on Tips&rdquo; deduction. If you work
                overtime or earn tips, your 2025 tax return will look different. Here&apos;s
                what actually changed — and what didn&apos;t.
            </p>

            <h2>What the deductions do</h2>
            <p>
                Both are <strong>above-the-line federal deductions</strong>, which means
                they lower your taxable income whether you itemize or take the standard
                deduction. They apply to tax years 2025 through 2028.
            </p>
            <ul>
                <li>
                    <strong>No Tax on Overtime:</strong> deduct up to $12,500 (single) /
                    $25,000 (married filing jointly) of the <em>premium portion</em> of
                    overtime pay.
                </li>
                <li>
                    <strong>No Tax on Tips:</strong> deduct up to $25,000 of qualified tip
                    income per return, for occupations that &ldquo;customarily and
                    regularly&rdquo; received tips before December 31, 2024.
                </li>
            </ul>

            <h2>The sneaky part: only the &ldquo;premium half&rdquo; of overtime counts</h2>
            <p>
                If your regular rate is $22/hour, your time-and-a-half overtime rate is
                $33/hour. Only the <em>extra $11/hour</em> — the half-time premium —
                qualifies for the deduction. You&apos;d have earned $22/hour anyway if
                it were a regular hour, so Congress is only exempting the premium.
            </p>
            <p>
                <strong>Formula:</strong> deductible overtime = (hourly rate × 0.5) ×
                overtime hours worked.
            </p>

            <h2>Real-dollar examples</h2>
            <h3>Example 1: $18/hour, 8 OT hours/week, no tips (single filer)</h3>
            <ul>
                <li>Annual regular wages (40 hrs × 50 wks × $18): $36,000</li>
                <li>OT full pay (8 hrs × 50 wks × $27): $10,800</li>
                <li>OT premium (deductible): $18 × 0.5 × 8 × 50 = <strong>$3,600</strong></li>
                <li>Marginal federal bracket: 12%</li>
                <li>Estimated annual savings: ~$432 (roughly $36/month back)</li>
            </ul>

            <h3>Example 2: $22/hour + $150/week in tips, 10 OT hours/week (single filer)</h3>
            <ul>
                <li>Annual gross (wages + OT + tips): ~$68,000</li>
                <li>OT premium (deductible): $22 × 0.5 × 10 × 50 = <strong>$5,500</strong></li>
                <li>Tips (deductible): $150 × 50 = <strong>$7,500</strong></li>
                <li>Total deductions: $13,000</li>
                <li>Marginal federal bracket: 22%</li>
                <li>Estimated annual savings: ~$2,185 (roughly $182/month back)</li>
            </ul>

            <h3>Example 3: $28/hour, 15 OT hours/week, no tips (married joint, spouse earns $50K)</h3>
            <ul>
                <li>Combined gross: ~$135,000</li>
                <li>OT premium (deductible): $28 × 0.5 × 15 × 50 = <strong>$10,500</strong></li>
                <li>Marginal federal bracket: 22%</li>
                <li>Estimated annual savings: ~$2,310 (roughly $193/month back)</li>
            </ul>

            <p>
                Run your own numbers in our{" "}
                <Link to="/calculator/overtime-tips">Overtime &amp; Tips Tax Calculator</Link>.
                It handles the premium-half logic and MAGI phaseout automatically.
            </p>

            <h2>What the OBBBA did NOT change</h2>
            <p>Three common misconceptions:</p>
            <ol>
                <li>
                    <strong>FICA still applies.</strong> Social Security (6.2%) and
                    Medicare (1.45%) are still withheld on every dollar of overtime and
                    tips. These deductions only affect <em>federal income tax</em>, not
                    payroll tax.
                </li>
                <li>
                    <strong>State income tax usually still applies.</strong> Only a few
                    states (Alabama, Louisiana, Idaho) have passed matching state
                    exemptions. Most states still tax your overtime and tips at state
                    level — check your state&apos;s conformity rules.
                </li>
                <li>
                    <strong>Your AGI doesn&apos;t drop.</strong> These are deductions
                    from taxable income, not reductions to Adjusted Gross Income. If
                    you qualify for credits that phase out by AGI (child tax credit, EITC,
                    healthcare subsidies), those calculations are unaffected.
                </li>
            </ol>

            <h2>Income phaseouts to watch</h2>
            <p>
                Both deductions start reducing at <strong>$150,000 MAGI</strong> (single)
                or <strong>$300,000</strong> (married joint). The overtime deduction
                reduces by $100 per $1,000 of income over threshold, fully zeroing out
                at $275,000 single / $550,000 joint. If you&apos;re above those lines,
                you get little to no benefit.
            </p>

            <h2>What you&apos;ll need at tax time</h2>
            <p>
                Employers are expected to report qualified overtime and qualified tips
                in separate W-2 box codes starting with tax year 2025. If your employer
                doesn&apos;t, you can self-compute using IRS Schedule 1-A (new for
                2025). Keep a simple log of overtime hours and tip income — even just a
                monthly text note to yourself — so you have a sanity check against
                whatever your employer reports.
            </p>

            <h2>Related tools</h2>
            <ul>
                <li><Link to="/calculator/overtime-tips">Overtime &amp; Tips Tax Calculator</Link> — plug in your numbers</li>
                <li><Link to="/calculator/income-tax">Federal Income Tax Calculator</Link> — see your bracket math</li>
                <li><Link to="/calculator/salary">Salary &amp; Hourly Calculator</Link> — convert between pay forms</li>
            </ul>
        </>
    );
}
